import React from 'react';
import { ContentfulCompetition } from '@jbhi-fi/apis/src/contentful/types';
import { CardCoverImage } from '../../Card';
import { CardGridCardProps } from '../../Card/CardGrid/CardGrid';
import { format, parseISO } from 'date-fns';
import { CardTransformer } from './CardTransformer';
import { MiniCardProps } from '../../MiniCard/MiniCard';
import { AssetTransformer } from './AssetTransformer';
import { ContentfulImage } from '../../ContentfulImage/ContentfulImage';
import { CompetitionStatusTileProps } from '../../CompetitionStatusTile/CompetitionStatusTile';

const contentfulSpaceIdNZ = 'ck9jtoelxwa6';

export abstract class CompetitionTransformer {
  static toCompetitionStatusTileProps = ({ fields }: ContentfulCompetition): CompetitionStatusTileProps => {
    const coverImage = fields?.heroImage?.fields ? AssetTransformer.toImageProps(fields.heroImage) : null;

    return {
      heading: fields?.heading ?? '',
      coverImageSrc: coverImage?.src ?? '',
      coverImageAlt: coverImage?.alt ?? '',
      termsHref: fields?.termsConditions?.fields ? fields.termsConditions.fields.file.url : '',
      isClosed: fields?.endDate ? new Date(fields?.endDate).getTime() <= Date.now() : false,
      closeDate: fields?.endDate
        ? `${format(parseISO(fields?.endDate), 'hh:mmaaaa d MMM yyyy').replace(/\./g, '')} (AEST)`
        : undefined,
      hasWinners: Boolean(fields?.winners),
      winnerAnnouncementDate: fields?.winnerAnnouncementDate
        ? format(parseISO(fields?.winnerAnnouncementDate), 'd MMM yyyy')
        : undefined,
      winners: fields?.winners,
    };
  };

  static toCardProps = (entry?: ContentfulCompetition): CardGridCardProps => {
    const coverImageProps = CardTransformer.toCardCoverImageProps(entry);
    return {
      heading: entry?.fields?.heading,
      cover: coverImageProps ? <CardCoverImage {...(coverImageProps ?? {})} /> : null,
      tagProps: CardTransformer.toTagProps(entry),
      footerProps: {
        leftContent: (
          <>
            <span style={{ fontWeight: 'bold' }}>Competition closes </span>
            <span>{this.toCloseDate(entry)}</span>
          </>
        ),
      },
      showTag: false,
      href: CardTransformer.toHref(entry),
    };
  };

  static toMiniCardProps = (entry?: ContentfulCompetition): MiniCardProps => {
    const coverImageProps = AssetTransformer.toContentfulImageProps(entry?.fields?.heroImage);
    return {
      title: entry?.fields?.heading || '',
      image: <ContentfulImage {...coverImageProps} sizes="150px" />,
      destinationUrl: CardTransformer.toHref(entry),
    };
  };

  static toCloseDate = (entry?: ContentfulCompetition): string => {
    const isNewZealand = entry?.sys.space?.sys.id === contentfulSpaceIdNZ;
    return entry?.fields?.endDate
      ? `${format(new Date(entry?.fields.endDate), 'hh:mmaaaa d MMM yyyy').replace(/\./g, '')} ${
          isNewZealand ? '(NZST)' : '(AEST)'
        }`
      : 'TBC';
  };
}
